import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import Zoom from 'chartjs-plugin-zoom';
import { convertDateToHourString, convertMillisToString, hexToRgba } from '../../common/ConvertTools.js';
import { getBootstrapColors } from '../race/RaceColors.js';
import EventBus from "../../common/EventBus";
import {
    handleMarkersDataReset,
    handleMarkersDataChart,
    addMarker,
    removeMarker,
    trimContentData,
    createContextMenu,
    onLegendClick,
    mergeAndInterpolateData,
    calculateCumulativeDistances,
    calculateTimeDifferenceByDistance,
    updateAxises
} from './PerfsLineChartHelpers';
import './chart-contextmenu.css';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Legend,
    Tooltip,
    Zoom,
);

let timeoutSetMarker = -1;

const PerfsLineChart = React.memo(({
    chartRef,
    contentData,
    isDesktop,
    filterData,
    contentRunSelected,
    setNewContentData,
    contentDataLap1,
    contentDataLap2,
    lapsSelected
}) => {

    console.log("PerfsLineChart / redraw");

    const markerClickThreshold = 10;

    const [contentDataLap2Tmp, setcontentDataLap2Tmp] = useState([]);

    const [visibleAxises, setVisibleAxises] = useState({ y: false, y2: false, y3: false });

    useEffect(() => {
        setcontentDataLap2Tmp(contentDataLap2);
    }, [contentDataLap2]);

    const contentDataRef = useRef(contentData);
    useEffect(() => {
        contentDataRef.current = contentData;
    }, [contentData]);

    const contentRunSelectedRef = useRef(contentRunSelected);
    useEffect(() => {
        contentRunSelectedRef.current = contentRunSelected;
    }, [contentRunSelected]);

    const [markers, setMarkers] = useState([]);
    const markerPositionsRef = useRef(markers);


    useEffect(() => {
        setcontentDataLap2Tmp(contentDataLap2);
    }, [contentDataLap2]);

    useEffect(() => {
        if (lapsSelected.length === 2 && contentDataLap1.length > 0 && contentDataLap2.length > 0) {
            const cumulativeDistancesLap1 = calculateCumulativeDistances(contentDataLap1);
            const cumulativeDistancesLap2 = calculateCumulativeDistances(contentDataLap2);

            const cumulativeTimesLap1 = contentDataLap1.map(point => point.date);
            const cumulativeTimesLap2 = contentDataLap2.map(point => point.date);

            const timeDifferences = calculateTimeDifferenceByDistance(
                cumulativeDistancesLap1,
                cumulativeDistancesLap2,
                cumulativeTimesLap1,
                cumulativeTimesLap2
            );

            console.log('Time Differences by Distance:', timeDifferences);
        }
    }, [contentDataLap1, contentDataLap2, lapsSelected]);


    const scrollToIndex = useCallback((index) => {
        const chart = chartRef.current;
        if (!chart || !chart.tooltip) return;

        const activeElements = chart.data.datasets
            .map((dataset, datasetIndex) => {
                const meta = chart.getDatasetMeta(datasetIndex);

                // Ensure meta and its data array are valid
                if (meta && meta.data && meta.data.length > 0) {
                    const element = meta.data[index];

                    // Ensure the element is defined and visible
                    if (element && dataset.hidden !== true && meta.hidden !== true) {
                        return { datasetIndex, index };
                    }
                }
                return null;
            })
            .filter((element) => element !== null);

        /*
        if (lapsSelected.length === 2) {
            const cumulativeDistancesLap1 = calculateCumulativeDistances(contentDataLap1);
            const cumulativeDistancesLap2 = calculateCumulativeDistances(contentDataLap2);

            const cumulativeTimesLap1 = contentDataLap1.map(point => point.date);
            const cumulativeTimesLap2 = contentDataLap2.map(point => point.date);

            const timeDifferences = calculateTimeDifferenceByDistance(
                cumulativeDistancesLap1,
                cumulativeDistancesLap2,
                cumulativeTimesLap1,
                cumulativeTimesLap2
            );

            let lapStartDiff = contentDataLap1[0].date - contentDataLap2[0].date;
            const lapDiff = lapStartDiff - timeDifferences[index];
            setLapTimeDiff(lapDiff);
            console.log('Time Difference at index:', lapDiff);
        }
        */

        // Only set active elements if the array is non-empty and valid
        if (activeElements.length > 0) {
            try {
                chart.tooltip.setActiveElements(activeElements);
                chart.draw();  // Use draw instead of update to improve performance
            } catch (error) {
                console.error("Error setting active elements:", error);
            }
        } else {
            console.log("No active elements to set.");
        }
    }, [chartRef]);


    useEffect(() => {
        const resetHandler = () => handleMarkersDataReset(setMarkers, markerPositionsRef);
        const chartHandler = (newMarkers) => handleMarkersDataChart(newMarkers, setMarkers, markerPositionsRef);
        const handleReplayUpdate = ({ index }) => scrollToIndex(index);

        EventBus.on("handleMarkersDataReset", resetHandler);
        EventBus.on('handleMarkersDataChart', chartHandler);
        EventBus.on('replayUpdate', handleReplayUpdate);

        return () => {
            EventBus.remove("handleMarkersDataReset", resetHandler);
            EventBus.remove('handleMarkersDataChart', chartHandler);
            EventBus.remove('replayUpdate', handleReplayUpdate);
        };
    }, [scrollToIndex]);


    const defaultVisibleDatasets = ["Speed", "Voltage", "Current"];

    const [visibleDatasets, setVisibleDatasets] = useState(defaultVisibleDatasets);
    const refreshVisibleDatasets = () => {
        let savedDataJson = localStorage.getItem('visibleDatasetsPerfs');
        let savedData;
        if (savedDataJson === null || savedDataJson === undefined) {
            savedData = defaultVisibleDatasets;
            localStorage.setItem('visibleDatasetsPerfs', JSON.stringify(defaultVisibleDatasets));
        } else {
            savedData = JSON.parse(savedDataJson);
        }
        setVisibleDatasets(savedData);
    }

    const updateCurrentMarker = useCallback((index) => {
        let currentItem1, currentItem2;
        if (lapsSelected.length === 0) {
            currentItem1 = contentData[index];
        } else if (lapsSelected.length > 0) {
            currentItem1 = contentData[index + markerPositionsRef.current[lapsSelected[0]]];
            if (lapsSelected.length > 1)
                currentItem2 = contentData[index + markerPositionsRef.current[lapsSelected[1]]];
        }

        if (!currentItem1) return;
        if (lapsSelected.length > 1 && !currentItem2) return;

        let lapDiff = 0;

        if (lapsSelected.length === 2) {
            const cumulativeDistancesLap1 = calculateCumulativeDistances(contentDataLap1);
            const cumulativeDistancesLap2 = calculateCumulativeDistances(contentDataLap2);

            const cumulativeTimesLap1 = contentDataLap1.map(point => point.date);
            const cumulativeTimesLap2 = contentDataLap2.map(point => point.date);

            const timeDifferences = calculateTimeDifferenceByDistance(
                cumulativeDistancesLap1,
                cumulativeDistancesLap2,
                cumulativeTimesLap1,
                cumulativeTimesLap2
            );

            if (contentDataLap1[0]?.date && contentDataLap2[0]?.date) {
                let lapStartDiff = contentDataLap1[0].date - contentDataLap2[0].date;
                lapDiff = lapStartDiff - timeDifferences[index];
                if (isNaN(lapDiff)) {
                    lapDiff = 0;
                }
            }
        }

        const pos1 = [currentItem1?.positionGps_longitude, currentItem1?.positionGps_latitude];
        const pos2 = [currentItem2 ? currentItem2.positionGps_longitude : 0, currentItem2 ? currentItem2.positionGps_latitude : 0, lapDiff];
        EventBus.dispatch('updateCurrentMarkersCoordinates', [pos1, pos2]);

    }, [contentData, contentDataLap1, contentDataLap2, lapsSelected]);

    const updateCurrentMarkerRef = useRef(updateCurrentMarker);
    useEffect(() => {
        updateCurrentMarkerRef.current = updateCurrentMarker;
    }, [updateCurrentMarker]);


    // diry patches ...
    useEffect(() => {
        refreshVisibleDatasets();
        console.log("PerfsLineChart / useEffect / visibleDatasets", visibleDatasets);
        setVisibleAxises(updateAxises(chartRef.current, visibleDatasets));
        if (chartRef.current)
            chartRef.current.update();
    }, []);

    useEffect(() => {
        refreshVisibleDatasets();
        console.log("PerfsLineChart / useEffect contentData / visibleDatasets", visibleDatasets);
        setVisibleAxises(updateAxises(chartRef.current, visibleDatasets));
        if (chartRef.current)
            chartRef.current.update();
    }, [contentData]);

    useEffect(() => {

        if (contentDataLap2.length == 0) return;
        if (lapsSelected.length < 2) return;

        const mergedData = mergeAndInterpolateData(contentDataLap1, contentDataLap2, ["voltage",
            "speed",
            "speedGps",
            "positionGps_altitude",
            "current",
            "power",
            "acceleration",
            "errors",
            "controllerTemp",
            "motorTemp",
            "dutyMaster",
            "dutySlave",
            "throttlePercent",
            "brakePercent",
            "currentMot1",
            "currentMot2"
        ]);
        console.log("PerfsLineChart / mergedData", mergedData);

        // Create a new array for contentDataLap2Tmp with updated voltage values
        const updatedContentDataLap2Tmp = contentDataLap2.map((item, index) => ({
            ...item,
            voltage: mergedData.data2.voltage[index],
            speed: mergedData.data2.speed[index],
            speedGps: mergedData.data2.speedGps[index],
            positionGps_altitude: mergedData.data2.positionGps_altitude[index],
            current: mergedData.data2.current[index],
            power: mergedData.data2.power[index],
            acceleration: mergedData.data2.acceleration[index],
            errors: mergedData.data2.errors[index],
            controllerTemp: mergedData.data2.controllerTemp[index],
            motorTemp: mergedData.data2.motorTemp[index],
            dutyMaster: mergedData.data2.dutyMaster[index],
            dutySlave: mergedData.data2.dutySlave[index],
            throttlePercent: mergedData.data2.throttlePercent[index],
            brakePercent: mergedData.data2.brakePercent[index],
            currentMot1: mergedData.data2.currentMot1[index],
            currentMot2: mergedData.data2.currentMot2[index]
        }));

        setcontentDataLap2Tmp(updatedContentDataLap2Tmp);

    }, [contentDataLap2, lapsSelected]);


    const getLabels = () => {
        if (lapsSelected.length == 0) {
            return contentData.map(item => [convertDateToHourString(item.date / 1000, true), " " + (item.distance.toFixed(0) * 10) + ' m'])
        }
        else if (lapsSelected.length == 1) {
            return contentDataLap1.map(item => [convertMillisToString((item.date - contentDataLap1[0].date), true), " " + (item.distance.toFixed(0) * 10) + ' m'])
        }
        else if (lapsSelected.length == 2 && contentDataLap1.length > 0 && contentDataLap2Tmp.length > 0) {
            const length1 = contentDataLap1[contentDataLap1.length - 1].date - contentDataLap1[0].date;
            const length2 = contentDataLap2Tmp[contentDataLap2Tmp.length - 1].date - contentDataLap2Tmp[0].date;

            if (length1 > length2)
                return contentDataLap1.map(item => convertMillisToString((item.date - contentDataLap1[0].date), true))
            else
                return contentDataLap2Tmp.map(item => convertMillisToString((item.date - contentDataLap2Tmp[0].date), true))
        }
    }

    let iColor = 0;
    const chartData = {
        labels: getLabels(),
        datasets: [
            {
                label: 'Speed',
                data: (lapsSelected.length > 0) ? contentDataLap1.map(item => item.speed) : contentData.map(item => item.speed),
                borderColor: getBootstrapColors()[iColor],
                backgroundColor: getBootstrapColors()[iColor],
                unit: "Km/h",
                hidden: !visibleDatasets.includes('Speed')
            },
            {
                label: 'Speed_sec',
                data: (lapsSelected.length > 1) ? contentDataLap2Tmp.map(item => item.speed?.toFixed(0)) : [],
                borderColor: getBootstrapColors()[iColor],
                borderDash: [2, 2],
                borderWidth: 2,
                backgroundColor: getBootstrapColors()[iColor++],
                unit: "Km/h",
                hidden: !visibleDatasets.includes('Speed')
            },
            {
                label: 'Speed Gps',
                data: (lapsSelected.length > 0) ? contentDataLap1.map(item => item.speedGps?.toFixed(1)) : contentData.map(item => item.speedGps?.toFixed(1)),
                borderColor: getBootstrapColors()[iColor],
                backgroundColor: getBootstrapColors()[iColor],
                unit: "Km/h",
                hidden: !visibleDatasets.includes('Speed Gps')
            },
            {
                label: 'Speed Gps_sec',
                data: (lapsSelected.length > 1) ? contentDataLap2Tmp.map(item => item.speedGps?.toFixed(1)) : [],
                borderColor: getBootstrapColors()[iColor],
                borderDash: [2, 2],
                borderWidth: 2,
                backgroundColor: getBootstrapColors()[iColor++],
                unit: "Km/h",
                hidden: !visibleDatasets.includes('Speed Gps')
            },
            {
                label: 'Altitude Gps',
                data: (lapsSelected.length > 0) ? contentDataLap1.map(item => item.positionGps_altitude?.toFixed(0)) : contentData.map(item => item.positionGps_altitude?.toFixed(0)),
                borderColor: getBootstrapColors()[iColor],
                borderDash: [2, 2],
                borderWidth: 2,
                backgroundColor: getBootstrapColors()[iColor++],
                unit: "m",
                hidden: !visibleDatasets.includes('Altitude Gps')
            },
            {
                label: 'Altitude Gps_sec',
                data: (lapsSelected.length > 1) ? contentDataLap2Tmp.map(item => item.positionGps_altitude?.toFixed(0)) : [],
                borderColor: getBootstrapColors()[iColor],
                borderDash: [2, 2],
                borderWidth: 2,
                backgroundColor: getBootstrapColors()[iColor++],
                unit: "m",
                hidden: !visibleDatasets.includes('Altitude Gps')
            },
            {
                label: 'Distance',
                data: (lapsSelected.length > 0) ? contentDataLap1.map(item => item.distance?.toFixed(0) * 10) : contentData.map(item => item.distance?.toFixed(0) * 10),
                borderColor: getBootstrapColors()[iColor],
                backgroundColor: getBootstrapColors()[iColor],
                unit: "m",
                yAxisID: 'y2',
                hidden: !visibleDatasets.includes('Distance')
            },
            {
                label: 'Distance_sec',
                data: (lapsSelected.length > 1) ? contentDataLap2Tmp.map(item => item.speedGps?.toFixed(0) * 10) : [],
                borderColor: getBootstrapColors()[iColor],
                borderDash: [2, 2],
                borderWidth: 2,
                backgroundColor: getBootstrapColors()[iColor++],
                unit: "m",
                yAxisID: 'y2',
                hidden: !visibleDatasets.includes('Distance')
            },
            {
                label: 'Voltage',
                data: (lapsSelected.length > 0) ? contentDataLap1.map(item => item.voltage.toFixed(1)) : contentData.map(item => item.voltage.toFixed(1)),
                borderColor: getBootstrapColors()[iColor],
                backgroundColor: getBootstrapColors()[iColor],
                unit: "V",
                hidden: !visibleDatasets.includes('Voltage')
            },
            {
                label: 'Voltage_sec',
                data: (lapsSelected.length > 1) ? contentDataLap2Tmp.map(item => item?.voltage?.toFixed(1)) : [],
                borderColor: getBootstrapColors()[iColor],
                borderDash: [2, 2],
                borderWidth: 2,
                backgroundColor: getBootstrapColors()[iColor++],
                unit: "V",
                hidden: !visibleDatasets.includes('Voltage')
            },
            {
                label: 'Current',
                data: (lapsSelected.length > 0) ? contentDataLap1.map(item => item.current) : contentData.map(item => item.current),
                borderColor: getBootstrapColors()[iColor],
                backgroundColor: getBootstrapColors()[iColor],
                unit: "A",
                hidden: !visibleDatasets.includes('Current')
            },
            {
                label: 'Current_sec',
                data: (lapsSelected.length > 1) ? contentDataLap2Tmp.map(item => item.current?.toFixed(1)) : [],
                borderColor: getBootstrapColors()[iColor],
                borderDash: [2, 2],
                borderWidth: 2,
                backgroundColor: getBootstrapColors()[iColor++],
                unit: "A",
                hidden: !visibleDatasets.includes('Current')
            },
            {
                label: 'Power',
                data: (lapsSelected.length > 0) ? contentDataLap1.map(item => item.power) : contentData.map(item => item.power),
                borderColor: getBootstrapColors()[iColor],
                backgroundColor: getBootstrapColors()[iColor],
                unit: "W",
                yAxisID: 'y2',
                hidden: !visibleDatasets.includes('Power')
            },
            {
                label: 'Power_sec',
                data: (lapsSelected.length > 1) ? contentDataLap2Tmp.map(item => item.power?.toFixed(0)) : [],
                borderColor: getBootstrapColors()[iColor],
                borderDash: [2, 2],
                borderWidth: 2,
                backgroundColor: getBootstrapColors()[iColor++],
                unit: "W",
                yAxisID: 'y2',
                hidden: !visibleDatasets.includes('Power')
            },
            {
                label: 'Acceleration',
                data: (lapsSelected.length > 0) ? contentDataLap1.map(item => item.acceleration) : contentData.map(item => item.acceleration),
                borderColor: getBootstrapColors()[iColor],
                backgroundColor: getBootstrapColors()[iColor],
                unit: "m/s²",
                yAxisID: 'y3',
                hidden: !visibleDatasets.includes('Acceleration')
            },
            {
                label: 'Acceleration_sec',
                data: (lapsSelected.length > 1) ? contentDataLap2Tmp.map(item => item.acceleration?.toFixed(2)) : [],
                borderColor: getBootstrapColors()[iColor],
                borderDash: [2, 2],
                borderWidth: 2,
                backgroundColor: getBootstrapColors()[iColor++],
                unit: "m/s²",
                yAxisID: 'y3',
                hidden: !visibleDatasets.includes('Acceleration')
            },
            {
                label: 'Errors',
                data: (lapsSelected.length > 0) ? contentDataLap1.map(item => item.errors) : contentData.map(item => item.errors),
                borderColor: getBootstrapColors()[iColor],
                backgroundColor: getBootstrapColors()[iColor],
                unit: "",
                hidden: !visibleDatasets.includes('Errors')
            },
            {
                label: 'Errors_sec',
                data: (lapsSelected.length > 1) ? contentDataLap2Tmp.map(item => item.errors) : [],
                borderColor: getBootstrapColors()[iColor],
                borderDash: [2, 2],
                borderWidth: 2,
                backgroundColor: getBootstrapColors()[iColor++],
                unit: "",
                hidden: !visibleDatasets.includes('Errors')
            },
            {
                label: 'Controller temp',
                data: (lapsSelected.length > 0) ? contentDataLap1.map(item => item.controllerTemp) : contentData.map(item => item.controllerTemp),
                borderColor: getBootstrapColors()[iColor],
                backgroundColor: getBootstrapColors()[iColor],
                unit: "°C",
                hidden: !visibleDatasets.includes('Controller temp')
            },
            {
                label: 'Controller temp_sec',
                data: (lapsSelected.length > 1) ? contentDataLap2Tmp.map(item => item.controllerTemp?.toFixed(0)) : [],
                borderColor: getBootstrapColors()[iColor],
                borderDash: [2, 2],
                borderWidth: 2,
                backgroundColor: getBootstrapColors()[iColor++],
                unit: "°C",
                hidden: !visibleDatasets.includes('Controller temp')
            },
            {
                label: 'Motor temp',
                data: (lapsSelected.length > 0) ? contentDataLap1.map(item => item.motorTemp) : contentData.map(item => item.motorTemp),
                borderColor: getBootstrapColors()[iColor],
                backgroundColor: getBootstrapColors()[iColor],
                unit: "°C",
                hidden: !visibleDatasets.includes('Motor temp')
            },
            {
                label: 'Motor temp_sec',

                data: (lapsSelected.length > 1) ? contentDataLap2Tmp.map(item => item.motorTemp?.toFixed(0)) : [],
                borderColor: getBootstrapColors()[iColor],
                borderDash: [2, 2],
                borderWidth: 2,
                backgroundColor: getBootstrapColors()[iColor++],
                unit: "°C",
                hidden: !visibleDatasets.includes('Motor temp')
            },
            {
                label: 'Duty master',
                data: (lapsSelected.length > 0) ? contentDataLap1.map(item => item.dutyMaster) : contentData.map(item => item.dutyMaster),
                borderColor: getBootstrapColors()[iColor],
                backgroundColor: getBootstrapColors()[iColor],
                unit: "",
                hidden: !visibleDatasets.includes('Duty master')
            },
            {
                label: 'Duty master_sec',
                data: (lapsSelected.length > 1) ? contentDataLap2Tmp.map(item => item.dutyMaster?.toFixed(1)) : [],
                borderColor: getBootstrapColors()[iColor],
                borderDash: [2, 2],
                borderWidth: 2,
                backgroundColor: getBootstrapColors()[iColor++],
                unit: "",
                hidden: !visibleDatasets.includes('Duty master')
            },
            {
                label: 'Duty slave',
                data: (lapsSelected.length > 0) ? contentDataLap1.map(item => item.dutySlave) : contentData.map(item => item.dutySlave),

                borderColor: getBootstrapColors()[iColor],
                backgroundColor: getBootstrapColors()[iColor],
                unit: "",
                hidden: !visibleDatasets.includes('Duty slave')
            },
            {
                label: 'Duty slave_sec',
                data: (lapsSelected.length > 1) ? contentDataLap2Tmp.map(item => item.dutySlave?.toFixed(1)) : [],
                borderColor: getBootstrapColors()[iColor],
                borderDash: [2, 2],
                borderWidth: 2,
                backgroundColor: getBootstrapColors()[iColor++],
                unit: "",
                hidden: !visibleDatasets.includes('Duty slave')
            },
            {
                label: 'Throttle',
                data: (lapsSelected.length > 0) ? contentDataLap1.map(item => item.throttlePercent) : contentData.map(item => item.throttlePercent),
                borderColor: getBootstrapColors()[iColor],
                backgroundColor: getBootstrapColors()[iColor],
                unit: "%",
                hidden: !visibleDatasets.includes('Throttle')
            },
            {
                label: 'Throttle_sec',
                data: (lapsSelected.length > 1) ? contentDataLap2Tmp.map(item => item.throttlePercent?.toFixed(0)) : [],
                borderColor: getBootstrapColors()[iColor],
                borderDash: [2, 2],
                borderWidth: 2,
                backgroundColor: getBootstrapColors()[iColor++],
                unit: "%",
                hidden: !visibleDatasets.includes('Throttle')
            },
            {
                label: 'Brake',
                data: (lapsSelected.length > 0) ? contentDataLap1.map(item => item.brakePercent) : contentData.map(item => item.brakePercent),
                borderColor: getBootstrapColors()[iColor],
                backgroundColor: getBootstrapColors()[iColor],
                unit: "%",
                hidden: !visibleDatasets.includes('Brake')
            },
            {
                label: 'Brake_sec',
                data: (lapsSelected.length > 1) ? contentDataLap2Tmp.map(item => item.brakePercent?.toFixed(0)) : [],
                borderColor: getBootstrapColors()[iColor],
                borderDash: [2, 2],
                borderWidth: 2,
                backgroundColor: getBootstrapColors()[iColor++],
                unit: "%",
                hidden: !visibleDatasets.includes('Brake')
            },
            {
                label: 'Current motor master',
                data: (lapsSelected.length > 0) ? contentDataLap1.map(item => item.currentMot1) : contentData.map(item => item.currentMot1),
                borderColor: getBootstrapColors()[iColor],
                backgroundColor: getBootstrapColors()[iColor],
                unit: "A",
                hidden: !visibleDatasets.includes('Current motor master')
            },
            {
                label: 'Current motor master_sec',
                data: (lapsSelected.length > 1) ? contentDataLap2Tmp.map(item => item.currentMot1?.toFixed(0)) : [],
                borderColor: getBootstrapColors()[iColor],
                borderDash: [2, 2],
                borderWidth: 2,
                backgroundColor: getBootstrapColors()[iColor++],
                unit: "A",
                hidden: !visibleDatasets.includes('Current motor master')
            },
            {
                label: 'Current motor slave',
                data: (lapsSelected.length > 0) ? contentDataLap1.map(item => item.currentMot2) : contentData.map(item => item.currentMot2),
                borderColor: getBootstrapColors()[iColor],
                backgroundColor: getBootstrapColors()[iColor],
                unit: "A",
                hidden: !visibleDatasets.includes('Current motor slave')
            },
            {
                label: 'Current motor slave_sec',
                data: (lapsSelected.length > 1) ? contentDataLap2Tmp.map(item => item.currentMot2?.toFixed(0)) : [],
                borderColor: getBootstrapColors()[iColor],
                borderDash: [2, 2],
                borderWidth: 2,
                backgroundColor: getBootstrapColors()[iColor++],
                unit: "A",
                hidden: !visibleDatasets.includes('Current motor slave')
            },
            {
                label: 'BMS int temp 1',
                data: contentData.map(item => item.bmsIntTemp1),
                borderColor: getBootstrapColors()[iColor],
                backgroundColor: getBootstrapColors()[iColor++],
                unit: "°C",
                hidden: !visibleDatasets.includes('BMS int temp 1')
            },
            {
                label: 'BMS int temp 2',
                data: contentData.map(item => item.bmsIntTemp2),
                borderColor: getBootstrapColors()[iColor],
                backgroundColor: getBootstrapColors()[iColor++],
                unit: "°C",
                hidden: !visibleDatasets.includes('BMS int temp 2')
            },
            {
                label: 'BMS bat temp 1',
                data: contentData.map(item => item.bmsBatTemp1),
                borderColor: getBootstrapColors()[iColor],
                backgroundColor: getBootstrapColors()[iColor++],
                unit: "°C",
                hidden: !visibleDatasets.includes('BMS bat temp 1')
            },
            {
                label: 'BMS bat temp 2',
                data: contentData.map(item => item.bmsBatTemp2),
                borderColor: getBootstrapColors()[iColor],
                backgroundColor: getBootstrapColors()[iColor++],
                unit: "°C",
                hidden: !visibleDatasets.includes('BMS bat temp 2')
            },
            {
                label: 'BMS current',
                data: contentData.map(item => item.bmsCurrent),
                borderColor: getBootstrapColors()[iColor],
                backgroundColor: getBootstrapColors()[iColor++],
                unit: "A",
                hidden: !visibleDatasets.includes('BMS current')
            },
        ],
    };

    const computedStyle = getComputedStyle(document.body);
    let gridColor = computedStyle.getPropertyValue('--color-middle-grey').trim();
    gridColor = hexToRgba(gridColor, 0.2);

    const optionsChart = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    usePointStyle: true,
                    filter: function (legendItem, data) {
                        return !legendItem.text.includes("_sec")
                    }

                },
                onClick: function (e, legendItem, data) {
                    onLegendClick(e, legendItem, setVisibleAxises, data);
                }
            },
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    label: function (context) {
                        var label = context.dataset.label || '';

                        // Ensure the parsed value is valid
                        if (!context || !context.parsed || context.parsed.y === undefined || context.parsed.y === null) {
                            return ''; // Return an empty string if the value is invalid
                        }

                        if (!context.dataset.hidden) { // Only add label for visible datasets
                            if ((lapsSelected.length == 2) && (!label.includes("_sec")))
                                label += ' lap' + (lapsSelected[0] + 1);
                            if ((lapsSelected.length == 2) && (label.includes("_sec")))
                                label = label.replace("_sec", "") + ' lap' + (lapsSelected[1] + 1);
                            if (label) {
                                label += ': ';
                            }
                            label += context.parsed.y + context.dataset.unit;
                        }
                        return label;
                    }
                },
                filter: function (tooltipItem) {
                    // Ensure only datasets with valid data are considered
                    return tooltipItem && tooltipItem.raw !== null && tooltipItem.raw !== undefined;
                },
            },
            zoom: {
                pan: {
                    enabled: isDesktop,
                    mode: 'x',
                },
                zoom: {
                    wheel: {
                        enabled: true,
                        mode: 'x',
                        speed: 0.1,
                        threshold: 2,
                        sensitivity: 3,
                    },
                    pan: {
                        enabled: true,
                        mode: 'x',
                    },
                    drag: {
                        enabled: isDesktop,
                        modifierKey: 'ctrl',
                    },
                    pinch: {
                        enabled: isDesktop
                    },
                    mode: "x",
                    scaleMode: 'x',
                },
            }
        },
        options: {
            interaction: {
                mode: 'index',
            },
            responsiveAnimationDuration: 0,
            spanGaps: true,
        },
        scales: {
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    autoSkipPadding: 50
                },
            },
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                grid: {
                    color: gridColor,
                },
                display: true,
            },
            y2: {
                id: 'y2',
                type: 'linear',
                display: true,
                position: 'right',
                grid: {
                    drawOnChartArea: false,
                },
                ticks: { callback: function (value) { return value + ' W'; } },
                display: visibleAxises.y2,
            },
            y3: {
                id: 'y3',
                type: 'linear',
                display: true,
                position: 'right',
                grid: {
                    drawOnChartArea: false,
                },
                suggestedMax: 0.1,
                ticks: { callback: function (value) { return value.toFixed(1) + ' m/s²'; } },
                display: visibleAxises.y3,
            },
        },
        elements: {
            point: {
                radius: 0,
            }
        },
        datasets: {
            line: {
                pointRadius: 0
            }
        },
        animation: false,
    };

    const customPluginChart = [
        {
            afterInit: (chart) => {
                (isDesktop) && chart.canvas.addEventListener('contextmenu', (event) => createContextMenu(event, chart, contentDataRef, contentRunSelectedRef, trimContentData, markerPositionsRef, setNewContentData));
            },
            afterDraw: (chart) => {
                const ctx = chart.ctx;
                if (!ctx) return;

                if (chart.data.datasets[0].data.length < 2) {
                    let width = chart.width;
                    let height = chart.height;
                    ctx.textAlign = "center";
                    ctx.textBaseline = "middle";
                    ctx.font = "16px Arial";
                    let msg = filterData && contentRunSelected.timestamp !== 0
                        ? "No data to display (filter is enabled)"
                        : "No data to display";
                    ctx.fillStyle = "#777";
                    ctx.fillText(msg, width / 2, height / 2);
                    ctx.restore();
                }

                if (timeoutSetMarker >= 0) clearInterval(timeoutSetMarker);

                if (chart?.tooltip?._active && chart?.tooltip?._active.length) {
                    const activePoint = chart?.tooltip?._active[0];
                    const x = activePoint.element.x;
                    const topY = chart.scales['y'].top;
                    const bottomY = chart.scales['y'].bottom;

                    ctx.save();
                    ctx.beginPath();
                    ctx.moveTo(x, topY);
                    ctx.lineTo(x, bottomY);
                    ctx.lineWidth = 1;
                    ctx.strokeStyle = localStorage.getItem('themeDark') === 'true'
                        ? 'rgba(255, 255, 255, 0.5)'
                        : 'rgba(0, 0, 0, 0.5)';
                    ctx.stroke();
                    ctx.restore();

                    updateCurrentMarkerRef.current(activePoint?.index);
                }

                const currentMarkerPositions = markerPositionsRef.current;
                currentMarkerPositions.forEach((index) => {
                    if (index >= 0 && index < contentDataRef.current.length) {
                        const xPos = chart.scales.x.getPixelForValue(index);

                        ctx.save();
                        ctx.beginPath();
                        ctx.moveTo(xPos, chart.chartArea.top);
                        ctx.lineTo(xPos, chart.chartArea.bottom);
                        ctx.strokeStyle = 'rgba(255, 0, 0, 1)';
                        ctx.lineWidth = 2;
                        ctx.stroke();
                        ctx.restore();
                    }
                });
            },
            afterEvent: (chart, evt) => {
                if (evt.event.type === "click" && evt.inChartArea && isDesktop) {
                    if (chart && chart?.tooltip?._active && chart?.tooltip?._active.length) {
                        const activePoint = chart?.tooltip?._active[0];
                        const clickedIndex = activePoint?.index;

                        if (clickedIndex !== undefined) {
                            let closestMarkerIndex = -1;
                            let minDistance = Infinity;

                            markerPositionsRef.current.forEach((markerIndex, idx) => {
                                const markerPos = chart.scales.x.getPixelForValue(markerIndex);
                                const clickPos = chart.scales.x.getPixelForValue(clickedIndex);
                                const distance = Math.abs(clickPos - markerPos);

                                if (distance < minDistance) {
                                    minDistance = distance;
                                    closestMarkerIndex = idx;
                                }
                            });

                            if (minDistance <= markerClickThreshold) {
                                removeMarker(closestMarkerIndex, markerPositionsRef);
                            } else {
                                addMarker(clickedIndex, markerPositionsRef);
                            }

                            chart.update();
                        }
                    }
                }
            }
        },
    ];

    return (
        <Line ref={chartRef} options={optionsChart} data={chartData} plugins={customPluginChart} />
    )
});

export default React.memo(PerfsLineChart);
